import { UserService } from '@services/user.service';
import { TranslateParser, TranslateService } from '@ngx-translate/core';
import { User } from '@models/user.model';
import { Component, OnInit, ViewChild } from '@angular/core';
import { TableConfig } from 'app/shared/util/table-config.utils';
import { ToastsManager } from "@app/shared/util/toastr.service";
import { environment } from "../../../../../environments/environment";
import { PermissionService } from "@app/authentication/services/permission.service";
import { Log } from "ng2-logger";
import { ConfirmationModalComponent } from "@app/shared/components";
import { UserBusinessType } from '@app/shared/models/enums/user-business-type.enum';
import { UserDetailsModalComponent } from '@app/roles/admin/pages/users/components/user-details-modal/user-details-modal.component';
import { RegisterEmdUserModalComponent } from '@app/roles/admin/pages/users/components/register-emd-user-modal/register-emd-user-modal.component';
import { EmdEmployee } from '@app/shared/models/emd-employee.model';

export enum Action {
    RESET,
    DELETE
}

@Component({
    selector: 'app-user-list',
    templateUrl: 'users.component.html',
    styleUrls: ['users.component.scss']
})
export class UserListComponent implements OnInit {

    _logger = Log.create(UserListComponent.name);

    @ViewChild(ConfirmationModalComponent, {static: true}) confirmationModal: ConfirmationModalComponent;
    @ViewChild(UserDetailsModalComponent, {static: true}) userDetailsModal: UserDetailsModalComponent;
    @ViewChild(RegisterEmdUserModalComponent, {static: true}) registerEmdUserModal: RegisterEmdUserModalComponent;

    isLoadingUsers = false;
    Action = Action;
    users = new Array<User>();
    emdEmployees = new Array<EmdEmployee>();
    userStates = [
        {name: 'All', value: 'All'},
        {name: 'Active', value: true},
        {name: 'Disabled', value: false}
    ];
    tableConfig = new TableConfig();
    selectedUser: User;
    userBusinessTypes = [];
    emdUserBusinessTypes = [];
    userBusinessTypeFilters = [];
    currentAction: Action;
    isConfirmationInProgress = false;
    isRegisteringEmdUser = false;

    constructor(private userService: UserService,
                private translateService: TranslateService,
                private toastr: ToastsManager,
                private permissionService: PermissionService,
                private translate: TranslateService,
                private translateParser: TranslateParser) {
        this.userStates = this.getUserStates();
        this.userBusinessTypes = this.getUserBusinessTypes();
        this.emdUserBusinessTypes = this.getEmdUserBusinessTypes();
        this.userBusinessTypeFilters = this.getUserBusinessTypeFilters();
        this.translateService.onLangChange.subscribe((event) => {
            this.userStates = this.getUserStates();
            this.userBusinessTypes = this.getUserBusinessTypes();
            this.emdUserBusinessTypes = this.getEmdUserBusinessTypes();
            this.userBusinessTypeFilters = this.getUserBusinessTypeFilters();
        });
    }

    ngOnInit() {
        this.getAll();
    }

    private getAll(tableConfig = this.tableConfig) {
        this.isLoadingUsers = true;
        this.userService.getUsers(tableConfig).subscribe(data => {
            this.isLoadingUsers = false;
            this.setUsers(data.users);
            this.tableConfig.pager.count = data.count;
        });
    }

    private setUsers(users) {
        this.users = users.map((user, index) => {
            user.index = this.getRealIndex(index);
            return user;
        });
    }

    private getRealIndex(index) {
        const pagination = this.tableConfig.pager;
        const startIndex = (pagination.page - 1) * pagination.pageSize + 1;
        return startIndex + index;
    }

    onUserUpdate(payload) {
        this.isLoadingUsers = true;
        this.userService.updateUser(payload.user, {
            isActive: payload.isActive,
            userBusinessType: payload.userBusinessType
        })
            .subscribe((data) => {
                this.handleSuccessfulUpdate(payload.user);
            });
    }

    openUserDetails(user: User) {
        this.userDetailsModal.open(user);
    }

    openRegisterEmdUserModal() {
        this.registerEmdUserModal.open();
    }

    onEmdEmployeesSearch(searchTerm: string) {
        if (!searchTerm) {
            this.emdEmployees = [];
            return;
        }

        this.userService.searchEmdEmployees(searchTerm).subscribe(
            (employees) => {
                this.emdEmployees = employees;
            },
            (error) => {
                this.emdEmployees = [];
            }
        );
    }

    onRegisterEmdUser(payload: { badgeId: number | null; userBusinessType: string }) {
        this.isRegisteringEmdUser = true;
        this.userService.registerEmdUser(payload).subscribe(
            (user) => {
                this.isRegisteringEmdUser = false;
                this.registerEmdUserModal.close();
                this.emdEmployees = [];
                this.getAll();
                this.toastr.success(this.translate.instant('admin.users.toasts.emdUserRegistered'));
            },
            (error) => {
                this.isRegisteringEmdUser = false;
            }
        );
    }

    openConfirmationModal(user, action) {
        this.currentAction = action;
        switch (action) {
            case this.Action.DELETE: {
                this.selectedUser = user;
                this.confirmationModal.title = this.translate.instant('admin.users.delete.modal.title');
                this.confirmationModal.message = this.translate.instant('admin.users.delete.modal.question');
                this.confirmationModal.open()
            }
                break;
            case this.Action.RESET: {
                this.selectedUser = user;
                this.confirmationModal.title = this.translate.instant('admin.users.reset.modal.title');
                this.confirmationModal.message = this.translate.instant('admin.users.reset.modal.question');
                this.confirmationModal.open()
            }
                break;
        }
    }

    // TODO good for demo, bad for general use
    private getUserStates() {
        return [
            {
                name: this.translate.instant('admin.users.table.filters.status.all'),
                value: 'All'
            },
            {
                name: this.translate.instant('admin.users.table.filters.status.active'),
                value: true
            },
            {
                name: this.translate.instant('admin.users.table.filters.status.disabled'),
                value: false
            }
        ];
    }

    private getUserBusinessTypes() {
        return [
            {
                name: this.translate.instant('admin.users.table.rows.userBusinessType.employeeUser'),
                value: UserBusinessType.EmployeeUser
            },
            {
                name: this.translate.instant('admin.users.table.rows.userBusinessType.technicalProductionUser'),
                value: UserBusinessType.TechnicalProductionUser
            },
            {
                name: this.translate.instant('admin.users.table.rows.userBusinessType.technicalToolManagementUser'),
                value: UserBusinessType.TechnicalToolManagementUser
            }
        ];
    }

    private getEmdUserBusinessTypes() {
        return this.getUserBusinessTypes().filter(item =>
            item.value === UserBusinessType.TechnicalProductionUser ||
            item.value === UserBusinessType.TechnicalToolManagementUser
        );
    }

    private getUserBusinessTypeFilters() {
        return [
            {
                name: this.translate.instant('admin.users.table.filters.userBusinessType.all'),
                value: 'All'
            },
            ...this.getUserBusinessTypes()
        ];
    }

    onConfirmAction() {
        switch (this.currentAction) {
            case Action.RESET: {
                this.userService.resetUser(this.selectedUser).subscribe(
                    (data) => {
                        this.userDetailsModal.close();
                        this.getAll()
                        this._logger.info("onConfirmUserReset - API Returned Data::", data);
                        this.toastr.info(this.translate.instant('admin.users.toasts.userResetSuccessfully'));
                    }, (error) => {
                        this._logger.error("onUserStatusUpdate - API Returned Error::", error);
                    }
                );
            }
                break;
            case Action.DELETE: {
                this.userService.deleteUser(this.selectedUser).subscribe(
                    (data) => {
                        this.userDetailsModal.close();
                        this.getAll()
                        this._logger.info("onConfirmUserReset - API Returned Data::", data);
                        this.toastr.info(this.translate.instant('admin.users.toasts.userDeleteSuccessfully'));
                    }, (error) => {
                        this._logger.error("onUserStatusUpdate - API Returned Error::", error);
                    }
                );
            }
                break;
        }

        this.confirmationModal.close();
    }

    onSortChange(tableState) {
        this.getAll(tableState);
    }

    onFilter(tableState) {
        this.getAll(tableState);
    }

    onPageChange(tableState) {
        this.getAll(tableState);
    }

    private handleSuccessfulUpdate(payload) {
        this.isLoadingUsers = false;
        this.userDetailsModal.close();
        this.getAll();
        this.toastr.success(this.buildUpdateMessage(payload), null, {toastLife: environment.toasts.duration});
    }

    private buildUpdateMessage(user) {
        return this.translateParser.interpolate(
            this.translate.instant('admin.users.toasts.updated'), {user: user.fullName});
    }

    shouldAllowUserEdit() {
        return this.permissionService.hasHasPermissionForFeature('MANAGE_USERS');
    }
}
